import {
  ANALYTICS_MAX_ARRAY_ITEMS,
  ANALYTICS_V2_VERSION,
  projectAttributionContext,
  validateCriteriaSnapshot,
  validateSelectionSnapshot,
  type ActionContext,
  type AnalyticsExecutionSnapshot,
  type AnalyticsRequestContext,
  type AnalyticsSurface,
  type AttributionContext,
  type CriteriaSnapshot,
  type EntityReference,
  type SearchContext,
  type SearchTrigger,
  type SelectionSnapshot,
  type WorkflowContext,
  type WorkflowType,
} from './contract-v2';

/** Frontend-only account scope. It is never an attribution header or authority. */
export interface AccountScope {
  readonly userId: string;
  readonly clientId?: number | null;
  readonly accountEpoch: string;
}

export interface BrowserContextIdentity extends AccountScope {
  readonly deviceId: string;
  readonly sessionId: number;
}

export type ScopedContext<T> = Readonly<T & { accountScope: AccountScope }>;
export type BrowserWorkflowContext = ScopedContext<WorkflowContext>;
export type BrowserSelectionContext = ScopedContext<WorkflowContext & SelectionSnapshot>;
/** A local entity selection, independent of a workflow's originating company selection. */
export type BrowserEntitySelection = ScopedContext<SelectionSnapshot>;
export type BrowserActionContext = ScopedContext<ActionContext>;
export type BrowserSearchContext = ScopedContext<SearchContext>;
export type BrowserExecutionSnapshot = AnalyticsExecutionSnapshot & {
  readonly accountEpoch: string;
};
export type RequestAnalyticsContext = AnalyticsRequestContext;

export type ContextDiagnostic =
  | 'missing_context'
  | 'schema_rejected'
  | 'storage_unavailable'
  | 'handoff_rejected'
  | 'handoff_unavailable';

export interface ContextRuntime {
  /** Must return undefined until the application has verified currentUser. */
  snapshot(): BrowserContextIdentity | undefined;
  touch(): void;
  newId(): string;
  diagnostic?(reason: ContextDiagnostic): void;
  now?(): number;
  storage?(): Storage | undefined;
}

export interface PageViewContext {
  readonly id: string;
  readonly path: string;
  readonly sessionId: number;
}

export interface WorkflowInput {
  readonly type: WorkflowType;
  readonly surface: AnalyticsSurface;
  readonly origin?: AttributionContext & { readonly search_id?: string };
  readonly parentEntity?: EntityReference;
  readonly companyType?: AttributionContext['source_company_type'];
  /** Explicitly starts another attempt even when its scope matches. */
  readonly reset?: boolean;
}

export interface SelectionInput {
  readonly entities: readonly EntityReference[];
}

export interface ActionInput {
  readonly surface: AnalyticsSurface;
  readonly context?: AttributionContext;
  readonly trigger?: SearchTrigger;
  readonly destination?: AttributionContext['destination'];
}

export interface SearchInput {
  readonly surface: AnalyticsSurface;
  readonly context?: AttributionContext;
  readonly action?: ActionContext;
  readonly parentEntity?: EntityReference;
  readonly criteriaVersion: string;
  readonly submittedCriteria?: CriteriaSnapshot;
  readonly trigger: SearchTrigger;
}

interface SurfaceRecord {
  workflow?: BrowserWorkflowContext;
  selection?: BrowserSelectionContext;
  search?: BrowserSearchContext;
  membership?: string;
}

export const CONTEXT_STORAGE_KEY = 'aydentify:analytics-context:v2';
/** Reserve the other half of the event budget for attribution, envelope and changed resource. */
export const SELECTION_SNAPSHOT_MAX_BYTES = 8000;
const MAX_SURFACES = 100;
const MAX_STORAGE_BYTES = 128 * 1024;
const MAX_SELECTION_MEMBERS = 10000;

/** Deep-freeze a JSON value we own, never an application object supplied by a caller. */
export function immutableContext<T>(value: T): Readonly<T> {
  if (value && typeof value === 'object') {
    for (const child of Object.values(value)) immutableContext(child);
    Object.freeze(value);
  }
  return value;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function jsonBytes(value: unknown): number {
  return encodeURIComponent(JSON.stringify(value)).replace(/%[0-9A-F]{2}|./g, 'x').length;
}

export function sameAccount(a?: AccountScope, b?: AccountScope): boolean {
  return !!a && !!b && a.userId === b.userId &&
    (a.clientId ?? null) === (b.clientId ?? null) && a.accountEpoch === b.accountEpoch;
}

function scopeFor(identity?: BrowserContextIdentity): AccountScope | undefined {
  if (!identity || !identity.userId || !identity.accountEpoch) return undefined;
  return immutableContext({
    userId: identity.userId,
    clientId: identity.clientId ?? null,
    accountEpoch: identity.accountEpoch,
  });
}

function surfaceKey(surface: AnalyticsSurface, parent?: EntityReference): string {
  return JSON.stringify([surface, parent?.namespace ?? null, parent?.id ?? null]);
}

/** Each browser tab has one store, with independent slots for every result surface/entity. */
export class AnalyticsContextStore {
  private account?: AccountScope;
  private page?: PageViewContext;
  private readonly records = new Map<string, SurfaceRecord>();
  // Full local membership never enters snapshots or storage; unreferenced selections can be GC'd.
  private entitySelections = new WeakMap<BrowserEntitySelection, string>();

  constructor(private readonly runtime: ContextRuntime) {}

  diagnostic(reason: ContextDiagnostic): void {
    try { this.runtime.diagnostic?.(reason); } catch { /* telemetry remains optional */ }
  }

  identity(): BrowserContextIdentity | undefined {
    try {
      const identity = this.runtime.snapshot();
      if (!scopeFor(identity)) return undefined;
      return identity;
    } catch {
      return undefined;
    }
  }

  currentAccount(): AccountScope | undefined {
    this.syncAccountScope();
    return this.account;
  }

  newId(): string { return this.runtime.newId(); }

  private storage(): Storage | undefined {
    try {
      return this.runtime.storage ? this.runtime.storage() : window.sessionStorage;
    } catch {
      this.diagnostic('storage_unavailable');
      return undefined;
    }
  }

  syncAccountScope(next = scopeFor(this.identity())): void {
    if (sameAccount(this.account, next) || (!this.account && !next)) return;
    const previous = this.account;
    this.records.clear();
    this.entitySelections = new WeakMap();
    this.page = undefined;
    this.account = next;
    if (next && !previous) this.restore();
    else this.persist();
  }

  clear(): void {
    this.records.clear();
    this.entitySelections = new WeakMap();
    this.page = undefined;
    this.account = undefined;
    try { this.storage()?.removeItem(CONTEXT_STORAGE_KEY); } catch {
      this.diagnostic('storage_unavailable');
    }
  }

  private persist(): void {
    try {
      const storage = this.storage();
      if (!storage) return;
      if (!this.account) { storage.removeItem(CONTEXT_STORAGE_KEY); return; }
      const records = [...this.records.entries()];
      let serialized = JSON.stringify({ version: 2, account: this.account, records });
      // A very large selection may remain in memory. Persistence never grows without bound.
      while (serialized.length > MAX_STORAGE_BYTES && records.length) {
        records.shift();
        serialized = JSON.stringify({ version: 2, account: this.account, records });
      }
      storage.setItem(CONTEXT_STORAGE_KEY, serialized);
    } catch {
      this.diagnostic('storage_unavailable');
    }
  }

  private restore(): void {
    try {
      const raw = this.storage()?.getItem(CONTEXT_STORAGE_KEY);
      if (!raw) return;
      if (raw.length > MAX_STORAGE_BYTES) throw new Error('bounded context');
      const saved = JSON.parse(raw);
      if (saved.version !== 2 || !sameAccount(saved.account, this.account)) return;
      if (!Array.isArray(saved.records) || saved.records.length > MAX_SURFACES) return;
      for (const entry of saved.records) {
        if (!Array.isArray(entry) || typeof entry[0] !== 'string' || entry[0].length > 512) continue;
        const record = entry[1];
        if (!record || typeof record !== 'object') continue;
        const restored: SurfaceRecord = {};
        if (this.validWorkflow(record.workflow)) restored.workflow = immutableContext(record.workflow);
        if (this.validSearch(record.search)) restored.search = immutableContext(record.search);
        if (restored.workflow && this.validSelection(record.selection) && typeof record.membership === 'string') {
          restored.selection = immutableContext(record.selection);
          restored.membership = record.membership;
        }
        if (restored.workflow || restored.search) this.records.set(entry[0], restored);
      }
    } catch {
      this.diagnostic('schema_rejected');
    }
  }

  private validScope(value: unknown, requireActive = true): boolean {
    const own = value && typeof value === 'object' && 'accountScope' in value
      ? (value as { accountScope: AccountScope }).accountScope : undefined;
    const workflowId = value && typeof value === 'object' && 'workflow_id' in value
      ? (value as { workflow_id?: string }).workflow_id : undefined;
    const selectionId = value && typeof value === 'object' && 'selection_id' in value
      ? (value as { selection_id?: string }).selection_id : undefined;
    const selectionCurrent = !requireActive || !workflowId || !selectionId ||
      ![...this.records.values()].some((record) => record.selection?.workflow_id === workflowId &&
        record.selection.selection_id !== selectionId);
    return (!own || sameAccount(own, this.account)) && selectionCurrent &&
      (!requireActive || !workflowId ||
        [...this.records.values()].some((record) => record.workflow?.workflow_id === workflowId));
  }

  private validWorkflow(value: any): value is BrowserWorkflowContext {
    return !!value && sameAccount(value.accountScope, this.account) &&
      typeof value.workflow_id === 'string' && !!value.workflow_type && !!value.surface &&
      typeof value.origin_page_view_id === 'string' && projectAttributionContext(value).valid;
  }

  private validSearch(value: any): value is BrowserSearchContext {
    return !!value && sameAccount(value.accountScope, this.account) &&
      typeof value.search_id === 'string' && value.search_id.length >= 5 &&
      typeof value.criteria_version === 'string' && value.criteria_version.length <= 128 &&
      !!value.surface && !!value.trigger && projectAttributionContext(value).valid &&
      (!value.submitted_criteria || validateCriteriaSnapshot(value.submitted_criteria).valid);
  }

  private validSelection(value: any): value is BrowserSelectionContext {
    const selection = value;
    if (!this.validWorkflow(value)) return false;
    return validateSelectionSnapshot({
      selection_id: selection.selection_id,
      selection_version: selection.selection_version,
      selected_count: selection.selected_count,
      included_count: selection.included_count,
      truncated: selection.truncated,
      entities: selection.entities,
    }).valid;
  }

  private save(key: string, record: SurfaceRecord): void {
    this.records.delete(key);
    if (this.records.size >= MAX_SURFACES) this.records.delete(this.records.keys().next().value!);
    this.records.set(key, record);
    this.persist();
  }

  setPageView(view: PageViewContext): void {
    this.syncAccountScope();
    if (!this.account) return;
    const validated = projectAttributionContext({ page_view_id: view.id });
    if (!validated.valid || !Number.isSafeInteger(view.sessionId) || view.sessionId <= 0) {
      this.diagnostic('schema_rejected'); return;
    }
    // Neither a URL query nor the navigation transport token is stored as a page identity.
    this.page = immutableContext({ ...view, path: view.path.split(/[?#]/, 1)[0] });
  }

  getPageView(): PageViewContext | undefined {
    this.syncAccountScope();
    return this.page;
  }

  beginWorkflow(input: WorkflowInput): BrowserWorkflowContext | undefined {
    this.syncAccountScope();
    if (!this.account || !this.validScope(input.origin)) return this.missing();
    const origin = input.origin || {};
    const originPage = origin.origin_page_view_id || origin.page_view_id || this.page?.id;
    if (!originPage) return this.missing();
    const key = surfaceKey(input.surface, input.parentEntity);
    const existing = this.records.get(key);
    const originSearch = origin.origin_search_id || origin.search_id;
    const reusable = !input.reset && existing?.workflow &&
      existing.workflow.workflow_type === input.type &&
      existing.workflow.origin_search_id === originSearch &&
      existing.workflow.source_company_type === input.companyType;
    if (reusable) return existing!.workflow;
    const projected = projectAttributionContext({
      ...origin,
      workflow_id: this.runtime.newId(),
      workflow_type: input.type,
      parent_workflow_id: origin.workflow_id,
      origin_page_view_id: originPage,
      origin_search_id: originSearch,
      page_view_id: this.page?.id || origin.page_view_id,
      surface: input.surface,
      source_surface: origin.surface || input.surface,
      source_company_type: input.companyType,
      parent_entity: input.parentEntity,
      // A newly created workflow cannot inherit an unrelated action or selection.
      action_id: undefined, attempt_id: undefined, selection_id: undefined,
      selection_version: undefined, selected_count: undefined,
      included_count: undefined, truncated: undefined, destination: undefined,
    });
    if (!projected.valid) return this.invalid();
    const workflow = immutableContext({ ...projected.value, accountScope: this.account }) as BrowserWorkflowContext;
    this.save(key, { ...existing, workflow, selection: undefined, membership: undefined });
    return workflow;
  }

  private selectionSnapshot(
    input: SelectionInput,
    previous?: SelectionSnapshot,
    previousMembership?: string,
  ): { snapshot: SelectionSnapshot; membership: string } | undefined {
    if (!Array.isArray(input.entities) || input.entities.length > MAX_SELECTION_MEMBERS) return this.invalid();
    const unique = new Map<string, EntityReference>();
    for (const entity of input.entities) {
      const checked = projectAttributionContext({ parent_entity: entity });
      if (!checked.valid || !checked.value.parent_entity) return this.invalid();
      unique.set(JSON.stringify([entity.namespace, entity.id]), checked.value.parent_entity);
    }
    const ordered = [...unique.entries()].sort(([a], [b]) => a.localeCompare(b));
    const membership = JSON.stringify(ordered.map(([id]) => id));
    const sameMembership = previous && previousMembership === membership;
    const entities = ordered.slice(0, ANALYTICS_MAX_ARRAY_ITEMS).map(([, entity]) => clone(entity));
    const snapshot = {
      selection_id: sameMembership ? previous.selection_id : this.runtime.newId(),
      selection_version: sameMembership ? previous.selection_version : (previous?.selection_version ?? 0) + 1,
      selected_count: unique.size,
      included_count: entities.length,
      truncated: unique.size > entities.length,
      entities,
    };
    while (entities.length && jsonBytes(snapshot) > SELECTION_SNAPSHOT_MAX_BYTES) {
      entities.pop();
      snapshot.included_count = entities.length;
      snapshot.truncated = true;
    }
    if (!validateSelectionSnapshot(snapshot).valid) return this.invalid();
    return { snapshot: immutableContext(snapshot), membership };
  }

  updateSelection(workflow: BrowserWorkflowContext, input: SelectionInput): BrowserSelectionContext | undefined {
    this.syncAccountScope();
    if (!this.account || !this.validWorkflow(workflow) || !this.validScope(workflow)) return this.missing();
    const key = surfaceKey(workflow.surface, workflow.parent_entity);
    const previous = this.records.get(key);
    if (previous?.workflow?.workflow_id !== workflow.workflow_id) return this.missing();
    const next = this.selectionSnapshot(input, previous.selection, previous.membership);
    if (!next) return undefined;
    const selection = immutableContext({ ...workflow, ...next.snapshot });
    if (selection.selected_count === 0) {
      const search = previous.search;
      if (search) this.save(key, { search });
      else { this.records.delete(key); this.persist(); }
      // This snapshot records the clear fact; it is no longer an active workflow context.
      return selection;
    }
    this.save(key, { ...previous, workflow: selection, selection, membership: next.membership });
    return selection;
  }

  /** Retain this scoped object locally; start with undefined after account change or restoration. */
  updateEntitySelection(previous: BrowserEntitySelection | undefined, input: SelectionInput): BrowserEntitySelection | undefined {
    this.syncAccountScope();
    if (!this.account || (previous && (!sameAccount(previous.accountScope, this.account) ||
      !this.entitySelections.has(previous)))) return this.missing();
    const next = this.selectionSnapshot(input, previous, previous && this.entitySelections.get(previous));
    if (!next) return undefined;
    const selection = immutableContext({ ...next.snapshot, accountScope: this.account });
    this.entitySelections.set(selection, next.membership);
    return selection;
  }

  getWorkflow(surface: AnalyticsSurface, parentEntity?: EntityReference): BrowserWorkflowContext | undefined {
    this.syncAccountScope();
    return this.records.get(surfaceKey(surface, parentEntity))?.workflow;
  }

  resetSurface(surface: AnalyticsSurface, parentEntity?: EntityReference): void {
    this.syncAccountScope();
    const key = surfaceKey(surface, parentEntity);
    this.records.delete(key);
    this.persist();
  }

  beginAction(input: ActionInput): BrowserActionContext | undefined {
    this.syncAccountScope();
    if (!this.account || !this.validScope(input.context)) return this.missing();
    const projected = projectAttributionContext({
      ...input.context,
      action_id: this.runtime.newId(),
      attempt_id: undefined,
      surface: input.surface,
      page_view_id: this.page?.id || input.context?.page_view_id,
      trigger: input.trigger,
      destination: input.destination,
    });
    if (!projected.valid) return this.invalid();
    return immutableContext({ ...projected.value, accountScope: this.account }) as BrowserActionContext;
  }

  createSearchContext(input: SearchInput): BrowserSearchContext | undefined {
    this.syncAccountScope();
    if (!this.account || !this.validScope(input.context) || !this.validScope(input.action)) return this.missing();
    if (!input.criteriaVersion || input.criteriaVersion.length > 128) return this.invalid();
    const criteria = input.submittedCriteria && validateCriteriaSnapshot(input.submittedCriteria);
    if (criteria && (!criteria.valid || criteria.value.criteria_version !== input.criteriaVersion)) return this.invalid();
    const parent = input.parentEntity || input.context?.parent_entity || input.action?.parent_entity;
    const key = surfaceKey(input.surface, parent);
    const record = this.records.get(key);
    const replacesOrigin = !!record?.search && record.workflow?.origin_search_id === record.search.search_id;
    const projected = projectAttributionContext({
      ...input.context,
      ...input.action,
      ...(replacesOrigin ? {
        workflow_id: undefined, workflow_type: undefined, parent_workflow_id: undefined,
        selection_id: undefined, selection_version: undefined, selected_count: undefined,
        included_count: undefined, truncated: undefined, source_company_type: undefined,
        origin_search_id: undefined, parent_search_id: undefined, origin_page_view_id: undefined,
        destination: undefined,
      } : {}),
      surface: input.surface,
      parent_entity: parent,
      page_view_id: this.page?.id || input.context?.page_view_id,
      trigger: input.trigger,
    });
    if (!projected.valid) return this.invalid();
    const search = immutableContext({
      ...projected.value,
      surface: input.surface,
      trigger: input.trigger,
      search_id: this.runtime.newId(),
      criteria_version: input.criteriaVersion,
      ...(criteria?.valid ? { submitted_criteria: criteria.value } : {}),
      accountScope: this.account,
    });
    // Replacing a result surface's originating applied search invalidates its selection attempt.
    this.save(key, { ...(!replacesOrigin ? record : {}), search });
    return search;
  }

  getSearchContext(surface: AnalyticsSurface, parentEntity?: EntityReference): BrowserSearchContext | undefined {
    this.syncAccountScope();
    return this.records.get(surfaceKey(surface, parentEntity))?.search;
  }

  captureExecution(input: RequestAnalyticsContext): BrowserExecutionSnapshot | undefined {
    this.syncAccountScope();
    const active = input.executionKind === 'interactive';
    // createSearchContext may detach a replaced origin workflow while preserving the accepted
    // action ID. Its new search snapshot is then the authority for that action's attribution.
    const sameSearchAction = !!input.search?.action_id && input.search.action_id === input.action?.action_id &&
      this.records.get(surfaceKey(input.search.surface, input.search.parent_entity))?.search?.search_id === input.search.search_id;
    if (!this.account || !this.validScope(input.search, active) ||
      !this.validScope(input.action, active && !sameSearchAction) ||
      !this.validScope(input.attribution, active && !sameSearchAction)) return this.missing();
    let identity: BrowserContextIdentity | undefined;
    if (input.executionKind === 'interactive') {
      if (!input.action?.action_id || !input.action.surface ||
        !projectAttributionContext(input.action).valid) return this.missing();
      this.runtime.touch();
      identity = this.identity();
      if (!sameAccount(scopeFor(identity), this.account)) return this.missing();
    } else {
      const prior = input.execution;
      if (!prior || !sameAccount(prior as AccountScope, this.account) ||
        typeof prior.deviceId !== 'string' || !Number.isSafeInteger(prior.sessionId)) return this.missing();
      if (prior.searchId && input.search && prior.searchId !== input.search.search_id) return this.missing();
      identity = prior as BrowserContextIdentity;
    }
    if (!identity) return this.missing();
    const initiating = input.executionKind === 'interactive' ? undefined : input.execution;
    const coordinates = sameSearchAction ? {
      surface: input.attribution?.surface || input.search?.surface,
      page_number: input.attribution?.page_number,
      page_size: input.attribution?.page_size,
      previous_page: input.attribution?.previous_page,
      offset: input.attribution?.offset,
      cursor_present: input.attribution?.cursor_present,
    } : input.attribution;
    const projected = projectAttributionContext({
      ...initiating?.attribution,
      ...input.search,
      ...(!sameSearchAction ? input.action : {}),
      ...coordinates,
      page_view_id: input.executionKind === 'interactive'
        ? this.page?.id || input.attribution?.page_view_id || input.search?.page_view_id
        : input.attribution?.page_view_id || initiating?.attribution.page_view_id,
      trigger: input.trigger || input.search?.trigger || input.attribution?.trigger || initiating?.attribution.trigger,
      execution_kind: input.executionKind,
    });
    if (!projected.valid) return this.invalid();
    const now = this.runtime.now?.() ?? Date.now();
    return immutableContext({
      userId: identity.userId,
      clientId: identity.clientId ?? null,
      accountEpoch: identity.accountEpoch,
      deviceId: identity.deviceId,
      sessionId: identity.sessionId,
      operationId: input.operationId || this.runtime.newId(),
      searchId: input.search?.search_id || initiating?.searchId,
      phase: input.phase,
      attribution: projected.value,
      schemaVersion: ANALYTICS_V2_VERSION,
      executionKind: input.executionKind,
      startedAt: now,
      capturedAt: now,
      excluded: initiating ? initiating.excluded : this.page?.path.startsWith('/user/') || false,
    });
  }

  /** Page arrival already owns activity renewal. Initial automatic loads only copy that identity. */
  capturePageExecution(attribution: AttributionContext = {}): BrowserExecutionSnapshot | undefined {
    this.syncAccountScope();
    const identity = this.identity();
    if (!identity || !this.account || !this.page || !this.validScope(attribution)) return this.missing();
    const projected = projectAttributionContext({
      ...attribution,
      page_view_id: this.page.id,
      trigger: 'initial_load',
      execution_kind: 'automatic',
    });
    if (!projected.valid) return this.invalid();
    const now = this.runtime.now?.() ?? Date.now();
    return immutableContext({
      userId: identity.userId,
      clientId: identity.clientId ?? null,
      accountEpoch: identity.accountEpoch,
      deviceId: identity.deviceId,
      sessionId: identity.sessionId,
      operationId: this.runtime.newId(),
      attribution: projected.value,
      schemaVersion: ANALYTICS_V2_VERSION,
      executionKind: 'automatic',
      startedAt: now,
      capturedAt: now,
      excluded: this.page.path.startsWith('/user/'),
    });
  }

  /** Navigation consumption explicitly activates a verified, token-bound workflow in this tab. */
  adoptNavigationContext(context: ScopedContext<AttributionContext>): boolean {
    this.syncAccountScope();
    if (!this.account || !sameAccount(context.accountScope, this.account)) return false;
    const projected = projectAttributionContext(context);
    if (!projected.valid) return false;
    if (!projected.value.workflow_id) return true;
    const { workflow_type, surface, origin_page_view_id } = projected.value;
    if (!workflow_type || !surface || !origin_page_view_id) return false;
    const key = surfaceKey(surface, projected.value.parent_entity);
    const workflow = immutableContext({ ...projected.value, accountScope: this.account }) as BrowserWorkflowContext;
    this.save(key, { ...this.records.get(key), workflow, selection: undefined, membership: undefined });
    return true;
  }

  private missing(): undefined { this.diagnostic('missing_context'); return undefined; }
  private invalid(): undefined { this.diagnostic('schema_rejected'); return undefined; }
}

let activeStore: AnalyticsContextStore | undefined;

/** Bound once by the shared analytics client; feature modules never initialize an SDK. */
export function configureContextRuntime(runtime: ContextRuntime): void {
  activeStore = new AnalyticsContextStore(runtime);
}

function optional<T>(work: () => T): T | undefined {
  try { return work(); } catch { activeStore?.diagnostic('schema_rejected'); return undefined; }
}

export const currentAccountScope = () => activeStore?.currentAccount();
export const currentContextIdentity = () => activeStore?.identity();
export const createContextId = () => activeStore?.newId();
export const reportContextDiagnostic = (reason: ContextDiagnostic) => activeStore?.diagnostic(reason);
export const syncAccountScope = (account?: AccountScope) => activeStore?.syncAccountScope(account);
export const clearAnalyticsContext = () => activeStore?.clear();
export const setPageView = (view: PageViewContext) => activeStore?.setPageView(view);
export const getPageView = () => activeStore?.getPageView();
export const beginWorkflow = (input: WorkflowInput) => optional(() => activeStore?.beginWorkflow(input));
export const updateSelection = (workflow: BrowserWorkflowContext, input: SelectionInput) => optional(() => activeStore?.updateSelection(workflow, input));
export const updateEntitySelection = (previous: BrowserEntitySelection | undefined, input: SelectionInput) => optional(() => activeStore?.updateEntitySelection(previous, input));
export const getWorkflowContext = (surface: AnalyticsSurface, parent?: EntityReference) => activeStore?.getWorkflow(surface, parent);
export const resetSurfaceContext = (surface: AnalyticsSurface, parent?: EntityReference) => activeStore?.resetSurface(surface, parent);
export const beginAction = (input: ActionInput) => optional(() => activeStore?.beginAction(input));
export const createSearchContext = (input: SearchInput) => optional(() => activeStore?.createSearchContext(input));
export const getSearchContext = (surface: AnalyticsSurface, parent?: EntityReference) => activeStore?.getSearchContext(surface, parent);
export const captureExecution = (input: RequestAnalyticsContext) => optional(() => activeStore?.captureExecution(input));
export const capturePageExecution = (attribution?: AttributionContext) => optional(() => activeStore?.capturePageExecution(attribution));
export const adoptNavigationContext = (context: ScopedContext<AttributionContext>) => optional(() => activeStore?.adoptNavigationContext(context)) ?? false;

/** Project a helper snapshot into the strict nested event field without local account/context state. */
export function selectionForEvent(context: SelectionSnapshot): SelectionSnapshot {
  return immutableContext({
    selection_id: context.selection_id,
    selection_version: context.selection_version,
    selected_count: context.selected_count,
    included_count: context.included_count,
    truncated: context.truncated,
    entities: clone(context.entities),
  });
}
